import { Plus, TestTube2 } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { CreateCaregiverModal } from '@apps/web/components/caregivers/create_caregiver_modal';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Label } from '@apps/web/components/ui/label';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { formatCaregiverName } from '@apps/web/lib/caregiver_name';
import { useMode } from '@apps/web/lib/mode_store';
import {
    useOrganization,
    useOrganizationCaregivers,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const TEST_PATIENT_CARE_PLANS: Record<string, string> = {
    'Patricia Davis': `**FALL RISK**

**Living Situation:** Lives alone in a two-story home with bedroom on main floor.

**Medical History:** Rheumatoid arthritis, hypertension, mild cognitive impairment

**Ambulation:** Contact guard assistance with rolling walker for all ambulation. Requires assistance with stairs.

**Allergies:** Penicillin, shellfish

**Pets:** One small dog (Shih Tzu) - needs to be let out to fenced backyard. Caregiver should not have dog allergies.

**Smoking:** Non-smoker

**Care Goals:**
- Maintain independence in familiar environment
- Fall prevention
- Social engagement

**Tasks:**
- Contact guard assistance for ambulation, transfers, and toileting
- Assistance with showering three times weekly, daily sponge bath on other days
- Full meal preparation for breakfast, lunch, and light dinner
- Medication reminders four times daily
- Light housekeeping including vacuuming, dusting, dishes, and laundry
- Let dog out every 3-4 hours and ensure water bowl is filled
- Assist with managing mail and paying bills as needed
- Encourage participation in puzzle books and TV programs for cognitive stimulation`,

    'Barbara Thomas': `**Living Situation:** Lives with adult daughter, Sarah, in apartment. Sarah is available to assist. No pets. Street parking is available, but the apartment is close to the train.

**Medical History:** Congestive heart failure, diabetes type 2, limited mobility

**Ambulation:** Wheelchair dependent. Requires two-person assist for transfers.

**Allergies:** Latex, adhesive tape

**Pets:** None

**Smoking:** Former smoker, quit 5 years ago

**Care Goals:**
- Support daily routine and comfort
- Maintain personal hygiene and dignity
- Quality of life

**Tasks:**
- Two-person assist for all transfers using gait belt (daughter assists when home)
- Complete assistance with bathing, dressing, toileting, and personal hygiene
- Meal preparation following dietary guidelines
- Light housekeeping including all household tasks
- Companionship and conversation - client loves discussing music as she was an opera singer
- Laundry and linen changes
- Grocery shopping and errands as needed
- Accompany to scheduled appointments`,

    'William Brown': `**Living Situation:** Lives with spouse in single-level ranch home.

**Medical History:** Stroke with right-sided weakness, expressive aphasia, depression

**Ambulation:** Ambulates independently with quad cane for short distances. Uses wheelchair for longer distances.

**Allergies:** Sulfa drugs

**Pets:** One outdoor cat that comes to porch for feeding. Caregiver should not have cat allergies.

**Smoking:** Non-smoker

**Care Goals:**
- Maximize communication and independence
- Prevent social isolation
- Caregiver respite for spouse

**Tasks:**
- Standby assistance with ambulation, transfers, and ADLs as needed
- Assistance with bathing and dressing (client can do some independently)
- Meal preparation with encouragement to participate in simple tasks
- Encourage use of communication board and practice speech exercises
- Medication reminders twice daily
- Light housekeeping and laundry
- Feed outdoor cat once daily
- Accompany on walks outside weather permitting (uses wheelchair)
- Provide companionship including cards, music, or looking through photo albums
- One approved outing weekly (library, park, or errands)`,

    'Elizabeth Taylor': `**Living Situation:** Lives alone in senior apartment community with emergency call system.

**Medical History:** Parkinson's disease, osteoarthritis, macular degeneration, anxiety

**Ambulation:** Uses rollator walker with frequent rest breaks. Requires standby assistance due to freezing episodes and shuffling gait.

**Allergies:** None known

**Pets:** No pets, but enjoys bird watching from window.

**Smoking:** Non-smoker

**Care Goals:**
- Maintain safety and dignity at home
- Reduce anxiety and promote calm environment
- Support participation in apartment activities

**Tasks:**
- Standby assistance for all ambulation with awareness of freezing episodes
- Full assistance with tub bath twice weekly including hair washing
- Assistance with dressing, particularly buttons, zippers, and shoes
- Meal preparation with adaptive utensils (weighted fork and spoon)
- Medication reminders on strict schedule
- Light hands-on assistance with toileting as needed
- Complete homemaker services
- Fill bird feeders outside window weekly
- Accompany to apartment community activities (bingo, music programs)
- Read mail aloud and assist with correspondence
- Use calm, patient approach during anxiety episodes
- Encourage deep breathing exercises and relaxation techniques`,

    'Michael Anderson': `**Living Situation:** Lives with wife in two-story home. Bedroom converted to main floor den. Door code will be provided for access to the home when wife is not home.

**Medical History:** ALS (Amyotrophic Lateral Sclerosis), limited mobility, uses power wheelchair

**Ambulation:** Non-ambulatory. Uses power wheelchair operated by hand control. Requires Hoyer lift for all transfers.

**Allergies:** None known

**Pets:** Two small indoor dogs (Yorkies) - wife manages their care.

**Smoking:** Non-smoker

**Care Goals:**
- Maximize comfort and quality of life
- Support wife/primary caregiver
- Maintain dignity

**Tasks:**
- Hoyer lift transfers from bed to power wheelchair, to recliner, and back (wife assists)
- Complete assistance with all ADLs including bathing, toileting, and personal hygiene
- Prepare pureed meals and thickened liquids
- Slow, careful feeding assistance (sitting upright, small bites)
- Oral care after each meal and as needed
- Medication reminders per schedule
- Range of motion exercises to all extremities twice daily
- Position changes every 2 hours when in bed or recliner
- Complete bed change and laundry
- Light housekeeping to maintain clean environment
- Provide companionship and read aloud or play music per client preference`,

    'Robert Smith': `**FALL RISK**

**Living Situation:** Lives alone in 3rd floor walk-up apartment in urban area. No elevator. Street parking only - no dedicated parking available. Entry via lockbox with key code.

**Medical History:** Hypertensive heart disease, osteoarthritis in knees, early-stage vascular dementia with short-term memory deficits

**Ambulation:** Independent with cane on level surfaces. Requires contact guard assistance for stairs due to knee arthritis and balance concerns.

**Allergies:** Aspirin

**Pets:** None

**Smoking:** Non-smoker

**Care Goals:**
- Cognitive support and memory compensation strategies
- Maintain independence in apartment
- Stair safety
- Social engagement and mental stimulation

**Tasks:**
- Contact guard assistance for stair navigation (3 flights) if client leaves apartment
- NO personal care required - client manages own bathing, dressing, and toileting independently
- Meal preparation for lunch and dinner with focus on heart-healthy, low-sodium options
- Light housekeeping including dishes, vacuuming, dusting, and trash removal
- Medication reminders and setup in daily pill organizer (client self-administers)
- Companionship activities: watching news programs together, working on crossword puzzles, discussing current events
- Gentle reminders for daily hygiene routine and light exercise (client does independently)
- Cognitive engagement through conversation and memory-supporting techniques
- Accompany on errands if needed (note parking challenges - may need to drop off and circle)
- Weekly refrigerator check and grocery list creation
- Monitor for changes in memory or confusion and report to family

**Special Considerations:**
- Caregiver must be comfortable with 3-flight stair climb multiple times
- Allow extra time for parking - street parking only, may need to walk several blocks
- Lockbox key code will be provided for entry
- Client's son visits weekly and has clear expectations for support
- No hands-on personal care - this is companionship with homemaker support`,

    'Jennifer Wilson': `**HIGH FALL RISK**

**Living Situation:** Lives with adult daughter in single-level home.

**Medical History:** Hypertension, macular degeneration (legally blind), recent subdural hematoma from fall affecting mobility, frequent episodes of sudden unsteadiness

**Ambulation:** Uses walker with close supervision required at all times due to high fall risk and sudden balance loss. Wheelchair available for longer distances. Stair lift installed for basement access.

**Allergies:** None known

**Pets:** Two dogs and one cat in home

**Smoking:** Non-smoker

**Care Goals:**
- Fall prevention and safety supervision
- Maximize quality of life despite vision limitations
- Social engagement and mental stimulation
- Support primary caregiver (daughter)
- Encourage safe mobility and participation in activities

**Tasks:**
- Close supervision and standby assistance for all ambulation - high fall risk
- Assist with transfers and ensure use of appropriate equipment (walker, wheelchair)
- Light hands-on assistance as needed for stability
- Assist with prescription glasses and ensure client is wearing them
- Meal preparation with heart-healthy options
- Encourage client to visit kitchen during meal prep to discuss cooking and recipes
- Light housekeeping including dishes, laundry, and maintaining clear walkways
- Companionship focused on conversation about cooking, recipes, and life experiences
- Encourage outdoor time in yard or porch (weather permitting) with close supervision
- Monitor for sudden unsteadiness and ensure safe positioning if one occurs
- Work collaboratively with daughter Cassie who will be available to assist
- Medication reminders as needed
- Be aware of pets and prevent tripping hazards - caregiver should have no pet allergies (two dogs and one cat in home)
- Report any changes in mobility, vision complaints, or increase in fall episodes

**Special Considerations:**
- Client experiences sudden episodes of unsteadiness - caregiver must stay within arm's reach during ambulation
- Vision severely limited due to macular degeneration - use verbal cues and ensure good lighting
- Daughter is primary caregiver and will collaborate on care
- Client is very social and enjoys meaningful conversation about her passion for cooking
- Safety is primary concern while maintaining dignity and engagement
- Caregiver must be comfortable around dogs and cats (no pet allergies)`,

    'Mary Johnson': `**Living Situation:** Lives alone in mobile home. One step to enter.

**Medical History:** Peripheral neuropathy, legally blind, hearing impaired (uses hearing aids)

**Ambulation:** Independent with cane on level surfaces. Requires contact guard for steps and uneven terrain.

**Allergies:** Codeine, iodine

**Pets:** One medium-sized dog (Beagle mix) - needs daily walk. Caregiver should not have dog allergies.

**Smoking:** Active smoker - smokes outside only, approximately half pack daily. Caregiver must be comfortable working in a home where client smokes outdoors.

**Care Goals:**
- Prevent falls related to vision and neuropathy
- Maintain independence
- Support daily routine

**Tasks:**
- Contact guard assistance for ambulation, especially outdoors and on step
- Setup assistance for bathing with grab bars available
- Assistance with grooming including nail care (cannot see well enough for trimming)
- Meal preparation with dietary considerations
- Ensure adequate hydration
- Medication reminders with verbal confirmation (client cannot see pills clearly)
- Daily foot inspection and documentation of any changes
- Complete housekeeping to maintain clear pathways
- Walk dog daily (15-20 minute walk)
- Accompany client outside for smoking breaks to prevent falls on step
- Read labels, mail, and bills aloud as needed
- Clean hearing aids weekly
- One grocery shopping trip weekly`,
};

type SandboxSetupModalProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly onComplete?: () => void;
};

export function SandboxSetupModal({
    open,
    onOpenChange,
    onComplete,
}: SandboxSetupModalProps) {
    type CreatedCaregiver = { readonly id: string; readonly name: string };
    const [numDays, setNumDays] = useState('7');
    const [shiftsPerDay, setShiftsPerDay] = useState('4');
    const [isGenerating, setIsGenerating] = useState(false);
    const [selectedCaregivers, setSelectedCaregivers] = useState<
        Array<CreatedCaregiver>
    >([]);
    const [isCaregiverModalOpen, setIsCaregiverModalOpen] = useState(false);
    const organizationId = useOrganizationId();
    const mode = useMode();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'sandbox-setup-modal',
    });

    const {
        data: caregivers,
        isLoading: isLoadingCaregivers,
        mutate: mutateCaregivers,
    } = useOrganizationCaregivers(organizationId, {
        reason: 'sandbox-setup-modal',
    });

    const displayCaregiverNamesLastFirst =
        organization?.general_settings?.display_caregiver_names_last_first ??
        false;

    useEffect(() => {
        if (!caregivers?.length) {
            return;
        }

        const availableIds = new Set(caregivers.map((c) => c.id));

        setSelectedCaregivers((prev) =>
            prev.filter((caregiver) => availableIds.has(caregiver.id)),
        );
    }, [caregivers]);

    const handleCaregiverSelectionChange = useCallback(
        (ids: Array<string>) => {
            const selected = ids
                .map((id) => caregivers?.find((c) => c.id === id))
                .filter((c): c is NonNullable<typeof c> => c !== undefined)
                .map((c) => ({
                    id: c.id,
                    name: formatCaregiverName(
                        c.name,
                        displayCaregiverNamesLastFirst,
                    ),
                }));

            setSelectedCaregivers(selected);
        },
        [caregivers, displayCaregiverNamesLastFirst],
    );

    const caregiverOptions = useMemo(() => {
        return (caregivers ?? []).map((caregiver) => ({
            value: caregiver.id,
            label: formatCaregiverName(
                caregiver.name,
                displayCaregiverNamesLastFirst,
            ),
        }));
    }, [caregivers, displayCaregiverNamesLastFirst]);

    const selectedCaregiverIds = selectedCaregivers.map((c) => c.id);

    const handleCaregiverModalComplete = useCallback(
        (createdCaregivers: Array<CreatedCaregiver>) => {
            if (!createdCaregivers.length) {
                return;
            }

            setSelectedCaregivers((prev) => {
                const existingIds = new Set(prev.map((c) => c.id));
                const newCaregivers = createdCaregivers
                    .filter((c) => !existingIds.has(c.id))
                    .map((c) => ({
                        id: c.id,
                        name: formatCaregiverName(
                            c.name,
                            displayCaregiverNamesLastFirst,
                        ),
                    }));
                return [...prev, ...newCaregivers];
            });

            mutateCaregivers();
        },
        [mutateCaregivers, displayCaregiverNamesLastFirst],
    );

    const handleGenerate = async (
        caregiversForGeneration: Array<CreatedCaregiver>,
    ) => {
        if (!organizationId || mode !== 'sandbox') {
            toast.error('Cannot generate test data', {
                persistNotification: true,
                notificationType: 'form_failure',
            });
            return;
        }

        setIsGenerating(true);

        try {
            const daysCount = parseInt(numDays, 10);
            const shiftsPerDayCount = parseInt(shiftsPerDay, 10);

            // Generate shifts - one per caregiver per day
            let shiftsCreated = 0;
            const now = new Date();
            const clientNames = [
                'Mary Johnson',
                'Robert Smith',
                'Patricia Davis',
                'Jennifer Wilson',
                'William Brown',
                'Elizabeth Taylor',
                'Michael Anderson',
                'Barbara Thomas',
            ];
            const clientIdsByName = new Map<string, string>();
            const uniqueClientNames = Array.from(new Set(clientNames));
            for (const clientName of uniqueClientNames) {
                const [firstName, ...lastNameParts] = clientName.split(' ');
                const lastName = lastNameParts.join(' ');
                const carePlan = TEST_PATIENT_CARE_PLANS[clientName];
                const clientResponse = await getApiClient().post({
                    url: '/organizations/{organization_id}/clients',
                    pathParams: { organization_id: organizationId },
                    body: {
                        first_name: firstName ?? '',
                        last_name: lastName,
                        care_plan: carePlan,
                        is_active: true,
                    },
                });
                clientIdsByName.set(clientName, clientResponse.id);
            }

            // Create one shift per day for each caregiver
            for (let day = 0; day < daysCount; day++) {
                for (
                    let caregiverIndex = 0;
                    caregiverIndex < caregiversForGeneration.length;
                    caregiverIndex++
                ) {
                    const caregiver = caregiversForGeneration[caregiverIndex];
                    if (!caregiver) {
                        continue;
                    }

                    // Stagger shift times throughout the day
                    const shiftHour = 8 + ((caregiverIndex * 2) % 12);
                    const shiftDate = new Date(now);
                    shiftDate.setDate(now.getDate() + day);
                    shiftDate.setHours(shiftHour, 0, 0, 0);

                    const endDate = new Date(shiftDate);
                    endDate.setHours(shiftDate.getHours() + 4);

                    const patientName =
                        clientNames[
                            (day + caregiverIndex) % clientNames.length
                        ]!;
                    const clientId = clientIdsByName.get(patientName)!;
                    await getApiClient().post({
                        url: '/organizations/{organization_id}/shifts',
                        pathParams: { organization_id: organizationId },
                        body: {
                            client_id: clientId,
                            patient_name: patientName,
                            shift_start_time: shiftDate.toISOString(),
                            shift_end_time: endDate.toISOString(),
                            patient_address:
                                '123 Test Street, Test City, NY 10001',
                            assigned_caregiver_id: caregiver.id,
                            frequency: 'once',
                        },
                    });
                    shiftsCreated++;
                }
            }

            // Create additional unassigned shifts if requested
            const additionalShifts = Math.max(
                0,
                shiftsPerDayCount - caregiversForGeneration.length,
            );
            for (let day = 0; day < daysCount; day++) {
                for (let shift = 0; shift < additionalShifts; shift++) {
                    const shiftDate = new Date(now);
                    shiftDate.setDate(now.getDate() + day);
                    shiftDate.setHours(14 + shift * 2, 0, 0, 0);

                    const endDate = new Date(shiftDate);
                    endDate.setHours(shiftDate.getHours() + 4);

                    const unassignedPatientName =
                        clientNames[
                            Math.floor(Math.random() * clientNames.length)
                        ]!;
                    const clientId = clientIdsByName.get(
                        unassignedPatientName,
                    )!;
                    await getApiClient().post({
                        url: '/organizations/{organization_id}/shifts',
                        pathParams: { organization_id: organizationId },
                        body: {
                            client_id: clientId,
                            patient_name: unassignedPatientName,
                            shift_start_time: shiftDate.toISOString(),
                            shift_end_time: endDate.toISOString(),
                            patient_address:
                                '456 Demo Ave, Test City, NY 10002',
                            frequency: 'once',
                        },
                    });
                    shiftsCreated++;
                }
            }

            toast.success(`Created ${shiftsCreated} test shifts`, {
                persistNotification: false,
            });

            // Close modal and trigger refresh
            onOpenChange(false);
            onComplete?.();
        } catch (error) {
            toast.error('Failed to generate test data', {
                persistNotification: true,
                notificationType: 'form_failure',
            });
            console.error(error);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Fragment>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-h-[700px] overflow-y-auto sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TestTube2 className="h-5 w-5" />
                            Generate Test Data
                        </DialogTitle>
                        <DialogDescription>
                            Create test data with real phone numbers. Sandbox
                            mode sends real SMS messages and calls, just like
                            live mode. All data is kept separate from live mode!
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="days" className="text-right">
                            Days ahead
                        </Label>
                        <Select
                            value={numDays}
                            onValueChange={setNumDays}
                            disabled={isGenerating}
                        >
                            <SelectTrigger id="days" className="col-span-3">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[3, 5, 7, 10, 14].map((num) => (
                                    <SelectItem
                                        key={num}
                                        value={num.toString()}
                                    >
                                        {num} days
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="shifts" className="text-right">
                            Total shifts/day
                        </Label>
                        <div className="col-span-3 space-y-1">
                            <Select
                                value={shiftsPerDay}
                                onValueChange={setShiftsPerDay}
                                disabled={isGenerating}
                            >
                                <SelectTrigger id="shifts">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[2, 3, 4, 5, 6].map((num) => (
                                        <SelectItem
                                            key={num}
                                            value={num.toString()}
                                        >
                                            {num} shifts per day
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-muted-foreground text-xs">
                                Each caregiver gets 1 shift/day. Extra shifts
                                will be unassigned.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-4 items-start gap-4">
                        <Label className="pt-2 text-right">Caregivers</Label>
                        <div className="col-span-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <MultiSelect
                                    options={caregiverOptions}
                                    selected={selectedCaregiverIds}
                                    onChange={handleCaregiverSelectionChange}
                                    placeholder={
                                        isLoadingCaregivers
                                            ? 'Loading caregivers...'
                                            : 'Select caregivers to include'
                                    }
                                    disabled={isGenerating || !organizationId}
                                    isLoading={isLoadingCaregivers}
                                    className="flex-1"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setIsCaregiverModalOpen(true)
                                    }
                                    disabled={!organizationId}
                                >
                                    <Plus className="h-4 w-4" />
                                    Create
                                </Button>
                            </div>
                            <p className="text-muted-foreground text-xs">
                                Chosen caregivers will receive generated shifts.
                                Leave empty to create unassigned shifts.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isGenerating}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={async () => {
                                await handleGenerate(selectedCaregivers);
                            }}
                            disabled={isGenerating}
                        >
                            {isGenerating ? 'Generating...' : 'Generate'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <CreateCaregiverModal
                open={isCaregiverModalOpen}
                onOpenChange={setIsCaregiverModalOpen}
                onComplete={handleCaregiverModalComplete}
            />
        </Fragment>
    );
}
